import { Injectable, signal, computed } from '@angular/core';

// Imports directos de todos los componentes de diálogo
import { LoginDialogComponent } from '../modules/dialogs/login-dialog/login-dialog.component';
import { RegisterFlowComponent } from '../modules/register-flow/register-flow.component';
import { OtpDialogComponent } from '../modules/dialogs/otp-dialog/otp-dialog.component';
import { EmailVerifyStepComponent } from '../modules/register-steps/email-verify-window/email_verify_window.component';
import { ConsentDialogComponent } from '../modules/dialogs/consent-dialog/consent-dialog.component';
import { DiffCountryDialogComponent } from '../modules/dialogs/diffcountry-dialog/diffcountry-dialog.component';
import { BlockedUserComponent } from '../modules/register-steps/blocked-user/blocked_user.component';
import { BlockedMedicalComponent } from '../modules/register-steps/non-medical/non_medical.component';
import { OtpSuggestDialogComponent } from '../modules/dialogs/otp-suggest/otpsuggestpwd-dialog.component';
import { OtpChangePasswordDialogComponent } from '../modules/dialogs/otp-changePassword/otpchangepwd-dialog.component';
import { UserExistComponent } from '../modules/register-steps/existent_user/user_exists.component';
import { wronguuidComponent } from '../modules/dialogs/nouuid-dialog/nouuid-dialog.component';

import { DialogType } from './navigation-flow.service';

/**
 * Mapa de tipos de diálogo a sus componentes correspondientes
 * Este helper evita imports dinámicos y dependencias circulares
 * 
 * @version Angular 19+ con inject() y signals
 */
@Injectable({
  providedIn: 'root'
})
export class DialogComponentMapperService {
  
  // Mapa de componentes usando Map (más eficiente que objeto literal)
  private readonly componentMap: ReadonlyMap<DialogType, any>;

  // Signal con la lista de tipos disponibles (reactivo)
  private readonly _availableTypes = signal<readonly DialogType[]>([]);
  
  // Computed signal para acceso a tipos disponibles
  public readonly availableTypes = this._availableTypes.asReadonly();

  constructor() {
    this.componentMap = this.initializeComponentMap();
    this._availableTypes.set(Array.from(this.componentMap.keys()));
  }

  /**
   * Inicializa el mapa de componentes
   */
  private initializeComponentMap(): ReadonlyMap<DialogType, any> {
    return new Map<DialogType, any>([
      ['login', LoginDialogComponent],
      ['register', RegisterFlowComponent],
      ['otp', OtpDialogComponent],
      ['emailVerify', EmailVerifyStepComponent],
      ['consent', ConsentDialogComponent],
      ['diffCountry', DiffCountryDialogComponent],
      ['blockedUser', BlockedUserComponent],
      ['blockedMedical', BlockedMedicalComponent],
      ['otpSuggest', OtpSuggestDialogComponent],
      ['otpChangePassword', OtpChangePasswordDialogComponent],
      ['userExists', UserExistComponent],
      ['wrongUuid', wronguuidComponent]
    ]);
  }

  /**
   * Obtiene el componente para un tipo de diálogo
   * 
   * @param dialogType Tipo de diálogo
   * @returns Componente del diálogo o null si no existe
   */
  public getComponent(dialogType: DialogType): any {
    return this.componentMap.get(dialogType) ?? null;
  }

  /**
   * Verifica si existe un componente para el tipo de diálogo
   * 
   * @param dialogType Tipo de diálogo
   * @returns true si existe el componente
   */
  public hasComponent(dialogType: DialogType): boolean {
    return this.componentMap.has(dialogType);
  }

  /**
   * Obtiene todos los tipos de diálogo disponibles
   * 
   * @returns Array de tipos de diálogo
   */
  public getAvailableDialogTypes(): readonly DialogType[] {
    return this._availableTypes();
  }
}
